import {FolderType} from '#/FolderTree/Folder';

import {assertUnreachable} from './types';

/**
 *
 * ensureTrailingSlash takes an url as string and ensures the return value has a slash at the end
 *
 * @param url a url with or without a trailing slash
 * @returns {string}  with a trailing comma
 */
const ensureTrailingSlash = (url: string): string =>
  url.endsWith('/') ? url : `${url}/`;

const buildSearchParams = (searchParamsKeyValue: {
  [key: string]: string | number | boolean | undefined;
}): string => {
  const searchParamString = Object.entries(searchParamsKeyValue)
    .filter(
      ([_, value]) =>
        value !== undefined && (typeof value !== 'boolean' || value)
    )
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  return searchParamString ? `?${searchParamString}` : '';
};

export enum ContactsView {
  details = 'details',
  relationships = 'relationships',
  relatedEntities = 'related-entities',
}

export enum CommunicationView {
  triage = 'triage',
  all = 'all',
}

export type Arguments =
  | {
      module: 'cockpit';
      view?: 'time-report' | 'fees-report';
      dateValue?: number;
      userId?: string;
      feeId?: string;
    }
  | {
      module: 'casus-overview';
      casusId: string;
    }
  | {
      module: 'casus';
      casusId?: string;
      action?:
        | 'create-natural-person-casus'
        | 'create-legal-entity-casus'
        | 'create-pseudonym-casus';
    }
  | {
      module: 'casus';
      action: 'create-casus-for-existing-client';
      clientId: string;
      clientName: string;
      groupId?: string;
      groupName?: string;
    }
  | {
      module: 'user-settings';
    }
  | {
      module: 'obligations';
      obligationId?: string;
      action?: 'create-obligation' | 'set-filters-all';
    }
  | {
      module: 'casus-logView';
      casusId: string;
      folderId?: string | FolderType.AllItems | FolderType.Trash;
      artifactId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-communication';
      casusId: string;
      folderId?: string | FolderType.AllItems | FolderType.Trash;
      emailId?: string;
      action?: 'create-email';
      sendToEmailAddress?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-documents';
      casusId: string;
      folderId?: string | FolderType.AllItems | FolderType.Trash;
      documentId?: string;
      documentVersionId?: string;
      action?: 'create-document-from-template' | 'open-document';
      clearFilters?: boolean;
    }
  | {
      module: 'casus-documents-submissions';
      casusId: string;
      submissionId: string;
      reloadSubmissionFolders?: boolean;
    }
  | {
      module: 'casus-contacts';
      casusId: string;
    }
  | {
      module: 'casus-settings';
      casusId: string;
    }
  | {
      module: 'casus-fees';
      casusId: string;
      feeId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-invoice-containers';
      casusId: string;
      invoiceContainerId?: string;
      reloadTable?: boolean;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-invoices';
      casusId: string;
      invoiceId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-payments';
      casusId: string;
      paymentId?: string;
      reloadTable?: boolean;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-client-money';
      casusId: string;
      bookingId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-notes';
      casusId: string;
      noteId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'casus-archives';
      casusId: string;
    }
  | {
      module: 'casus-tasks';
      casusId: string;
      taskId?: string;
      action?: 'create-task';
      clearFilters?: boolean;
      reloadData?: boolean;
    }
  | {
      module: 'invoicing';
      view: 'invoice-containers';
      invoiceContainerId?: string;
      preselectedContainerIds?: string[];
      reloadTable?: boolean;
      clearFilters?: boolean;
    }
  | {
      module: 'invoicing';
      view: 'invoices';
      invoiceId?: string;
      reminderId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'invoicing';
      view?: undefined;
    }
  | {
      module: 'invoicing';
      view: 'transactions';
    }
  | {
      module: 'invoicing';
      view: 'payments';
      paymentId?: string;
      reloadTable?: boolean;
      clearFilters?: boolean;
    }
  | {
      module: 'invoicing';
      view: 'finance-export';
      exportId?: string;
      receiptId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'invoicing';
      view: 'client-money';
      bookingId?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'communication';
      emailId?: string;
      view?: `${CommunicationView}`;
      action?: 'create-email';
      sendToEmailAddress?: string;
      clearFilters?: boolean;
    }
  | {
      module: 'tasks';
      taskId?: string;
      clearFilters?: boolean;
      reloadData?: boolean;
    }
  | {
      module: 'time-tracking';
      dateValue?: number;
      activityId?: string;
      action?: 'create-activity' | 'edit-activity';
      userId?: string;
    }
  | {
      module: 'settings';
      view?:
        | 'templates'
        | 'placeholder-documentation'
        | 'finance-export'
        | 'bankaccount-management'
        | 'financial-institution-management'
        | 'user-management'
        | 'role-management'
        | 'speciality-fields'
        | 'office-locations'
        | 'vat-numbers'
        | 'tag-management'
        | 'archiving-locations'
        | 'hourly-rates'
        | 'email-account-management'
        | 'email-client-management'
        | 'document-locks';
    }
  | {
      module: 'settings';
      view: 'casus-templates';
      casusTemplateId?: string;
      reloadTable?: boolean;
    }
  | {
      module: 'contacts';
      contactId?: string;
      view?: `${ContactsView}`;
      action?: 'create-natural-person' | 'create-legal-entity';
      clearFilters?: boolean;
    }
  | {
      module: 'search';
      entityId?: string;
      preselectedCasusId?: string;
      clearFilters?: boolean;
    };

// eslint-disable-next-line complexity
export const getRouteUrl = (args: Arguments): string => {
  switch (args.module) {
    case 'cockpit':
      return `${ensureTrailingSlash(
        `/cockpit/${args.view ?? ''}`
      )}${buildSearchParams({
        [globalSearchParams.cockpit.timeReport.dateValue]: args.dateValue,
        [globalSearchParams.cockpit.timeReport.userId]: args.userId,
        [globalSearchParams.cockpit.timeReport.feeId]: args.feeId,
      })}`;
    case 'casus':
      if (args.action === 'create-casus-for-existing-client') {
        return `${ensureTrailingSlash(`/casus/`)}${buildSearchParams({
          [globalSearchParams.action]: args.action,
          [globalSearchParams.casus.create.groupId]: args.groupId,
          [globalSearchParams.casus.create.clientId]: args.clientId,
          [globalSearchParams.casus.create.clientName]: args.clientName,
          [globalSearchParams.casus.create.groupName]: args.groupName,
        })}`;
      } else {
        return `${ensureTrailingSlash(
          `/casus/${args.casusId ?? ''}`
        )}${buildSearchParams({
          [globalSearchParams.action]: args.action,
        })}`;
      }
    case 'casus-communication':
      return `${ensureTrailingSlash(
        `/casus/${args.casusId}/communication/${
          args.folderId ?? FolderType.AllItems
        }`
      )}${buildSearchParams({
        [globalSearchParams.action]: args.action,
        [globalSearchParams.communication.sendToEmailAddress]:
          args.sendToEmailAddress,
        [globalSearchParams.communication.activeEmailId]: args.emailId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-documents':
      return `${ensureTrailingSlash(
        `/casus/${args.casusId}/documents/${
          args.folderId ?? FolderType.AllItems
        }`
      )}${buildSearchParams({
        [globalSearchParams.action]: args.action,
        [globalSearchParams.documents.activeDocumentId]: args.documentId,
        [globalSearchParams.documents.activeDocumentVersionId]:
          args.documentVersionId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-documents-submissions':
      return `/casus/${args.casusId}/documents/submissions/${args.submissionId}/${buildSearchParams(
        {
          [globalSearchParams.justitia.reloadSubmissionFolders]:
            args.reloadSubmissionFolders ? 'true' : undefined,
        }
      )}`;
    case 'casus-archives':
      return `/casus/${args.casusId}/archives/`;
    case 'invoicing':
      if (args.view === 'invoice-containers') {
        return `/invoicing/invoice-containers/${buildSearchParams({
          [globalSearchParams.invoicing.invoiceContainers.containerId]:
            args.invoiceContainerId,
          [globalSearchParams.invoicing.invoiceContainers.reloadTable]:
            args.reloadTable ? 'true' : undefined,
          [globalSearchParams.invoicing.invoiceContainers
            .preselectedContainerIds]: args.preselectedContainerIds?.join(','),
          [globalSearchParams.clearFilters]: args.clearFilters,
        })}`;
      }
      if (args.view === 'invoices') {
        return `/invoicing/invoices/${buildSearchParams({
          [globalSearchParams.invoicing.invoices.invoiceId]: args.invoiceId,
          [globalSearchParams.invoicing.invoices.reminderId]: args.reminderId,
          [globalSearchParams.clearFilters]: args.clearFilters,
        })}`;
      }
      if (args.view === 'transactions') {
        return '/invoicing/transactions/';
      }
      if (args.view === 'payments') {
        return `/invoicing/payments/${buildSearchParams({
          [globalSearchParams.invoicing.payments.paymentId]: args.paymentId,
          [globalSearchParams.invoicing.payments.reloadTable]: args.reloadTable,
          [globalSearchParams.clearFilters]: args.clearFilters,
        })}`;
      }

      if (args.view === 'finance-export') {
        return `/invoicing/finance-export/${buildSearchParams({
          [globalSearchParams.invoicing.financeExport.exportId]: args.exportId,
          [globalSearchParams.invoicing.financeExport.receiptId]:
            args.receiptId,
          [globalSearchParams.clearFilters]: args.clearFilters,
        })}`;
      }
      if (args.view === 'client-money') {
        return `/invoicing/client-money/${buildSearchParams({
          [globalSearchParams.invoicing.clientMoney.bookingId]: args.bookingId,
          [globalSearchParams.clearFilters]: args.clearFilters,
        })}`;
      }
      return '/invoicing/';
    case 'obligations':
      return `${ensureTrailingSlash(
        `/obligations/${args.obligationId ?? ''}`
      )}${buildSearchParams({
        [globalSearchParams.action]: args.action,
      })}`;
    case 'casus-tasks':
      return `/casus/${args.casusId}/tasks-management/${buildSearchParams({
        [globalSearchParams.action]: args.action,
        [globalSearchParams.tasks.activeTaskId]: args.taskId,
        [globalSearchParams.clearFilters]: args.clearFilters,
        [globalSearchParams.tasks.reloadData]: args.reloadData,
      })}`;
    case 'casus-contacts':
      return `/casus/${args.casusId}/contacts/`;
    case 'casus-settings': {
      return `/casus/${args.casusId}/settings/`;
    }
    case 'casus-overview':
      return `/casus/${args.casusId}/overview/`;
    case 'casus-fees':
      return `/casus/${args.casusId}/fees/${buildSearchParams({
        [globalSearchParams.invoicing.fees.activeFeeId]: args.feeId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-invoice-containers':
      return `/casus/${args.casusId}/invoice-containers/${buildSearchParams({
        [globalSearchParams.invoicing.invoiceContainers.containerId]:
          args.invoiceContainerId,
        [globalSearchParams.invoicing.invoiceContainers.reloadTable]:
          args.reloadTable ? 'true' : undefined,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-invoices':
      return `/casus/${args.casusId}/invoices/${buildSearchParams({
        [globalSearchParams.invoicing.invoices.invoiceId]: args.invoiceId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-payments':
      return `/casus/${args.casusId}/payments/${buildSearchParams({
        [globalSearchParams.invoicing.payments.paymentId]: args.paymentId,
        [globalSearchParams.invoicing.payments.reloadTable]: args.reloadTable,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-client-money':
      return `/casus/${args.casusId}/client-money/${buildSearchParams({
        [globalSearchParams.invoicing.clientMoney.bookingId]: args.bookingId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-logView':
      return `${ensureTrailingSlash(
        `/casus/${args.casusId}/log-view/${
          args.folderId ?? FolderType.AllItems
        }`
      )}${buildSearchParams({
        [globalSearchParams.logView.activeArtifactId]: args.artifactId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'casus-notes':
      return `${ensureTrailingSlash(
        `/casus/${args.casusId}/notes/`
      )}${buildSearchParams({
        [globalSearchParams.notes.activeNoteId]: args.noteId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'communication':
      return `${ensureTrailingSlash(
        `/communication/${args.view ?? ''}`
      )}${buildSearchParams({
        [globalSearchParams.action]: args.action,
        [globalSearchParams.communication.sendToEmailAddress]:
          args.sendToEmailAddress,
        [globalSearchParams.communication.activeEmailId]: args.emailId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    case 'tasks':
      return `${ensureTrailingSlash(`/tasks`)}${buildSearchParams({
        [globalSearchParams.tasks.activeTaskId]: args.taskId,
        [globalSearchParams.clearFilters]: args.clearFilters,
        [globalSearchParams.tasks.reloadData]: args.reloadData,
      })}`;
    case 'time-tracking':
      return `${ensureTrailingSlash(
        `/time-tracking/${args.dateValue ?? ''}`
      )}${buildSearchParams({
        [globalSearchParams.action]: args.action,
        [globalSearchParams.timeTracking.activeActivityId]: args.activityId,
        [globalSearchParams.timeTracking.userId]: args.userId,
      })}`;
    case 'settings':
      if (args.view === 'casus-templates') {
        return `/settings/${args.view}/${buildSearchParams({
          [globalSearchParams.settings.casusTemplate.activeCasusTemplateId]:
            args.casusTemplateId,
          [globalSearchParams.settings.casusTemplate.reloadTable]:
            args.reloadTable ? 'true' : undefined,
        })}`;
      }
      return `${ensureTrailingSlash(`/settings/${args.view ?? ''}`)}`;
    case 'contacts': {
      const actionSuffix = buildSearchParams({
        [globalSearchParams.action]: args.action,
        [globalSearchParams.clearFilters]: args.clearFilters,
      });
      if (!args.contactId) {
        return `/contacts/${actionSuffix}`;
      }
      return (
        ensureTrailingSlash(`/contacts/${args.contactId}/${args.view ?? ''}`) +
        actionSuffix
      );
    }
    case 'user-settings':
      return '/user/user-settings/';
    case 'search':
      return `/search/${buildSearchParams({
        [globalSearchParams.search.activeEntityId]: args.entityId,
        [globalSearchParams.search.preselectedCasusId]: args.preselectedCasusId,
        [globalSearchParams.clearFilters]: args.clearFilters,
      })}`;
    default:
      assertUnreachable(args);
  }
};

export const globalSearchParams = {
  action: 'action',
  clearFilters: 'clearFilters',
  folders: {reload: 'reload-folders'},
  invoicing: {
    financeExport: {
      exportId: 'activeFinanceExportId',
      receiptId: 'activeReceiptId',
    },
    clientMoney: {bookingId: 'activeBookingId'},
    payments: {
      paymentId: 'paymentId',
      reloadDetail: 'reloadDetail',
      reloadTable: 'reloadTable',
    },
    invoiceContainers: {
      containerId: 'activeInvoiceContainerId',
      reloadDetail: 'reloadDetail',
      reloadTable: 'reloadTable',
      preselectedContainerIds: 'preselectedContainerIds',
    },
    invoices: {
      invoiceId: 'activeInvoiceId',
      reminderId: 'reminderId',
      reloadDetail: 'reloadDetail',
      reloadTable: 'reloadTable',
    },
    fees: {
      activeFeeId: 'activeFeeId',
    },
  },
  casus: {
    contacts: {openInvoicesOf: 'openInvoicesOf'},
    create: {
      clientId: 'clientId',
      groupId: 'groupId',
      clientName: 'clientName',
      groupName: 'groupName',
    },
  },
  communication: {
    activeEmailId: 'activeEmailId',
    sendToEmailAddress: 'to',
  },
  documents: {
    activeDocumentId: 'activeDocumentId',
    activeDocumentVersionId: 'activeDocumentVersionId',
    reload: 'reload',
  },
  logView: {
    activeArtifactId: 'activeArtifactId',
  },
  notes: {
    activeNoteId: 'activeNoteId',
  },
  timeTracking: {
    userId: 'userId',
    activeActivityId: 'activeActivityId',
    activeSuggestionActivityId: 'activeSuggestionActivityId',
  },
  cockpit: {
    timeReport: {
      dateValue: 'dateValue',
      userId: 'userId',
      feeId: 'feeId',
    },
  },
  settings: {
    casusTemplate: {
      activeCasusTemplateId: 'activeCasusTemplateId',
      reloadTable: 'reloadTable',
      reloadDetail: 'reloadDetail',
    },
    bankAccountManagement: {
      activeBankAccountId: 'activeBankAccountId',
    },
    financialInstitutionManagement: {
      activeFinancialInstitutionId: 'activeFinancialInstitutionId',
    },
    roleManagement: {
      activeRoleId: 'activeRoleId',
    },
    emailAccountManagement: {
      activeEmailAccountId: 'activeEmailAccountId',
    },
    userManagement: {
      activeUserId: 'activeUserId',
    },
    officeLocations: {
      activeOfficeLocationId: 'activeOfficeLocationId',
    },
    vatNumbers: {
      activeVatNumberId: 'activeVatNumberId',
    },
    archivingLocations: {
      activeArchivingLocationId: 'activeArchivingLocationId',
    },
    hourlyRates: {
      activeHourlyRateId: 'activeHourlyRateId',
    },
  },
  search: {
    activeEntityId: 'activeEntityId',
    preselectedCasusId: 'preselectedCasusId',
  },
  tasks: {
    activeTaskId: 'activeTaskId',
    reloadData: 'reloadData',
  },
  justitia: {
    reloadSubmissionFolders: 'reloadSubmissionFolders',
  },
} as const;

export const allowedSearchQueryActions = {
  casus: {
    createCasusForNaturalPerson: 'create-natural-person-casus',
    createCasusForLegalEntity: 'create-legal-entity-casus',
    createCasusForPseudonym: 'create-pseudonym-casus',
    createCasusForExistingClient: 'create-casus-for-existing-client',
  },
  communications: {
    createEmail: 'create-email',
  },
  contacts: {
    createNaturalPerson: 'create-natural-person',
    createLegalEntity: 'create-legal-entity',
    cloneContact: 'clone-contact',
  },
  documents: {
    createDocumentFromTemplate: 'create-document-from-template',
    openDocument: 'open-document',
  },
  obligations: {
    setFiltersToAll: 'set-filters-all',
    createObligation: 'create-obligation',
  },
  timeTracking: {
    createActivity: 'create-activity',
    editActivity: 'edit-activity',
    editActivitySuggestion: 'edit-activity-sugggestion',
  },
  justitia: {
    createSubmission: 'create-submission',
  },
} as const;
